import { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { View, StyleSheet } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { MotiView } from "moti";
import TabNavigator from "../components/common/general/TabNavigator/TabNavigator";
import { useAppConfig } from "../AppConfigProvider";
import UsersTabContainer from "../containers/settings/UsersTabContainer/UsersTabContainer";
import CoursesTabContainer from "../containers/settings/CoursesTabContainer/CoursesTabContainer";
import IntegrationsTabContainer from "../containers/settings/IntegrationsTabContainer/IntegrationsTabContainer";
import ProfileTabContainer from "../containers/settings/ProfileTabContainer/ProfileTabContainer";
import FacultyProfileTabContainer from "../containers/settings/FacultyProfileTabContainer/FacultyProfileTabContainer";
import { RolePermissions } from "../constants/rolesAndPermissions";
import { useGetUserPermissionsQuery } from "../graphql/generated/graphql";
import DegreesTabContainer from "../containers/settings/DegreesTabContainer/DegreesTabContainer";
import DefaultLayout from "../layouts/DefaultLayout.web";

function SettingsView() {
  const posthog = usePostHog();
  const { isMedium } = useResponsiveScreen();
  const { isAxioOffering, hasDegrees, primaryColor } = useAppConfig();

  const [activeTab, setActiveTab] = useState("profile");

  const { data } = useGetUserPermissionsQuery();

  const rolePermissions = data?.getUserPermissions ?? [];

  const tabs = [{ key: "profile", title: "Profile" }];
  if (rolePermissions.includes(RolePermissions.facultyView)) {
    tabs.push({ key: "users", title: "Users" });
  }
  if (rolePermissions.includes(RolePermissions.courseView)) {
    tabs.push({ key: "courses", title: "Manage Courses" });
  }
  if (rolePermissions.includes(RolePermissions.degreeView) && hasDegrees) {
    tabs.push({ key: "degrees", title: "Manage Degrees" });
  }

  useEffect(() => {
    posthog?.capture("Settings Page Visited");
  }, []);

  return (
    <DefaultLayout pageHasChat={false}>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={10}
        transition={{
          all: { type: "timing", duration: 500 },
        }}>
        <View style={styles.container}>
          <View style={styles.tabContainer}>
            <CustomText
              useTranslationText={false}
              text={"Settings"}
              size={isMedium ? "l" : "xs"}
              textType="display"
              weight="ultraBold"
              role="heading"
              aria-level="1"
              style={undefined}
            />
          </View>
        </View>
      </MotiView>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={500}
        transition={{
          all: { type: "timing", duration: 600 },
        }}>
        <CustomSpacing type="vertical" size="s" />
        {isAxioOffering ? (
          <TabNavigator
            tabData={tabs}
            activeTab={activeTab}
            onTabPress={(key) => {
              setActiveTab(key);
            }}
            activeTabColor={primaryColor}
            rightButtonComponent={undefined}
          />
        ) : null}
        <CustomSpacing type="vertical" size="l" />
      </MotiView>
      {activeTab === "profile" ? (
        rolePermissions.includes(RolePermissions.facultyView) ? (
          <FacultyProfileTabContainer />
        ) : (
          <ProfileTabContainer />
        )
      ) : null}
      {activeTab === "users" && rolePermissions.includes(RolePermissions.facultyView) ? <UsersTabContainer /> : null}
      {activeTab === "courses" && rolePermissions.includes(RolePermissions.courseView) ? <CoursesTabContainer /> : null}
      {activeTab === "integrations" ? <IntegrationsTabContainer /> : null}
      {activeTab === "degrees" && rolePermissions.includes(RolePermissions.degreeView) && hasDegrees ? (
        <DegreesTabContainer />
      ) : null}
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  tabContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
});

export default SettingsView;
